import { useMemo, useState, type CSSProperties } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  Empty,
  Input,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  AuditOutlined,
  BankOutlined,
  CameraOutlined,
  CheckCircleOutlined,
  CopyOutlined,
  DeleteOutlined,
  FolderAddOutlined,
  FormOutlined,
  GroupOutlined,
  HolderOutlined,
  IdcardOutlined,
  InboxOutlined,
  MobileOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ScreenDiagnostic, ScreenVocabulary } from '@/api/screens';
import { errorAt, under } from './diagnostics';
import { JourneySettings } from './JourneySettings';
import { KindSpecEditor } from './KindSpecEditor';
import { LocalizedField } from './LocalizedField';
import {
  CHECK_PRESETS,
  FIELD_TYPE_LABELS,
  KIND_COPY,
  SLOT_COPY,
  duplicateScreen,
  fieldNameFrom,
  newScreen,
  screenFromPreset,
  slugify,
  uniqueId,
  withTitle,
  withTitleTranslation,
  withUiText,
  type JourneyDef,
  type PromptField,
  type ScreenDef,
} from './model';

const { Text } = Typography;

function kindIcon(kind?: string) {
  switch ((kind ?? '').toUpperCase()) {
    case 'UPLOAD':
      return <InboxOutlined />;
    case 'FORM':
      return <FormOutlined />;
    case 'DECLARATION':
      return <AuditOutlined />;
    case 'PARTIES':
      return <TeamOutlined />;
    case 'CAPTURE':
      return <CameraOutlined />;
    case 'OTP':
      return <MobileOutlined />;
    case 'IDENTIFIER':
      return <IdcardOutlined />;
    case 'BANK_DETAILS':
      return <BankOutlined />;
    case 'AADHAAR_OTP':
      return <SafetyCertificateOutlined />;
    default:
      return <CheckCircleOutlined />;
  }
}

/** The gate's findings for one place, in its own words. The server is the only judge of legality. */
function Findings({ items, base }: { items: ScreenDiagnostic[]; base: string }) {
  if (items.length === 0) return null;
  return (
    <div data-testid="screen-findings">
      {items.map((d, i) => (
        <div key={`${d.code}-${i}`} style={{ fontSize: 12, marginBottom: 2 }}>
          <Text type={d.severity === 'ERROR' ? 'danger' : 'warning'}>
            <WarningOutlined /> {d.message}
          </Text>{' '}
          <Text type="secondary" className="ol-mono" style={{ fontSize: 10.5 }}>
            {d.path.slice(base.length) || d.code}
          </Text>
        </div>
      ))}
    </div>
  );
}

/**
 * The VISUAL journey builder (owner feedback 2026-08-09: "without visual components what am I
 * building?"; v2 bar: "think FigJam, not textarea"): a component palette, a drag-sortable screen
 * list with add / duplicate / delete, and a property editor per component — no JSON required
 * anywhere on this path. The palette is the server's closed vocabulary of ten kinds (OL-24), with a
 * property editor per kind; every dropdown is a server list, FORM fields are typed and groupable
 * under named sections, and the gate's findings come back on the control concerned. Reordering is
 * drag-and-drop with a full keyboard path (focus the handle, Space lifts, arrows move, Space drops).
 */
export function BuilderPanel({
  value,
  onChange,
  vocabulary,
  diagnostics = [],
}: {
  value: JourneyDef;
  onChange: (next: JourneyDef) => void;
  vocabulary: ScreenVocabulary;
  /** The gate's latest findings on this definition. */
  diagnostics?: ScreenDiagnostic[];
}) {
  const screens = useMemo(() => value.screens ?? [], [value.screens]);
  const [selected, setSelected] = useState(0);
  const [editingJourney, setEditingJourney] = useState(false);
  const current: ScreenDef | undefined = screens[selected];
  const locales = value.locales ?? ['en'];
  const screenPath = `/screens/${selected}`;
  const select = (index: number) => {
    setEditingJourney(false);
    setSelected(index);
  };

  const sensors = useSensors(
    // A 4px activation distance keeps plain clicks selecting, not dragging.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const patch = (updates: Partial<JourneyDef>) => onChange({ ...value, ...updates });
  const patchScreen = (index: number, updates: Partial<ScreenDef>) => {
    const next = screens.slice();
    next[index] = { ...next[index], ...updates };
    patch({ screens: next });
  };

  const appendScreen = (screen: ScreenDef) => {
    const next = [...screens, screen];
    patch({ screens: next });
    select(next.length - 1);
  };
  const addScreen = (kind: string) => appendScreen(newScreen(kind, screens));
  const addPreset = (family: string) => {
    const preset = CHECK_PRESETS.find((p) => p.family === family);
    if (preset) appendScreen(screenFromPreset(preset, screens));
  };
  const replaceScreen = (index: number, screen: ScreenDef) => {
    const next = screens.slice();
    next[index] = screen;
    patch({ screens: next });
  };

  const duplicate = (index: number) => {
    const copy = duplicateScreen(screens[index], screens);
    const next = [...screens.slice(0, index + 1), copy, ...screens.slice(index + 1)];
    patch({ screens: next });
    select(index + 1);
  };

  const removeScreen = (index: number) => {
    const next = screens.filter((_, i) => i !== index);
    patch({ screens: next });
    setSelected(Math.max(0, Math.min(selected, next.length - 1)));
  };

  // Sortable identity: index-based ids are stable for the DURATION OF ONE DRAG (the list only
  // reorders at drop), which is all dnd-kit needs — and they survive half-authored screens
  // whose own ids are still blank or duplicated.
  const sortIds = useMemo(() => screens.map((_, i) => `screen-${i}`), [screens]);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = sortIds.indexOf(String(active.id));
    const to = sortIds.indexOf(String(over.id));
    if (from < 0 || to < 0) return;
    patch({ screens: arrayMove(screens, from, to) });
    // Selection follows the journey, not the slot.
    if (selected === from) setSelected(to);
    else if (from < selected && to >= selected) setSelected(selected - 1);
    else if (from > selected && to <= selected) setSelected(selected + 1);
  };

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* ---- left: the journey (ordered components) + the palette ---- */}
      <div style={{ width: 300, flex: 'none' }}>
        <Card
          size="small"
          onClick={() => setEditingJourney(true)}
          data-testid="journey-settings-card"
          style={{
            cursor: 'pointer',
            marginBottom: 10,
            borderColor: editingJourney ? 'var(--ol-primary)' : undefined,
          }}
          styles={{ body: { padding: '8px 10px' } }}
        >
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
            <SettingOutlined /> Header, progress, languages
            {(errorAt(diagnostics, '/journey') || errorAt(diagnostics, '/locales')) && (
              <WarningOutlined style={{ color: 'var(--ol-negative, #d4380d)', marginInlineStart: 'auto' }} />
            )}
          </div>
        </Card>
        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Journey — the screens in order
        </Text>
        {screens.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No screens yet — add the first component below"
            style={{ margin: '12px 0' }}
          />
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={sortIds} strategy={verticalListSortingStrategy}>
            <Space direction="vertical" size={6} style={{ width: '100%' }}>
              {screens.map((s, i) => (
                <SortableScreenCard
                  key={sortIds[i]}
                  sortId={sortIds[i]}
                  screen={s}
                  index={i}
                  selected={!editingJourney && i === selected}
                  hasError={errorAt(diagnostics, `/screens/${i}`)}
                  onSelect={() => select(i)}
                  onDuplicate={() => duplicate(i)}
                  onDelete={() => removeScreen(i)}
                />
              ))}
            </Space>
          </SortableContext>
        </DndContext>

        <Text strong style={{ fontSize: 12, display: 'block', margin: '16px 0 8px' }}>
          Add a component
        </Text>
        <Select
          showSearch
          style={{ width: '100%', marginBottom: 8 }}
          aria-label="Add from a check"
          data-testid="preset-select"
          placeholder="From a check: PAN, selfie, UPI…"
          value={null}
          optionFilterProp="label"
          options={CHECK_PRESETS.map((p) => ({ value: p.family, label: p.label }))}
          onChange={(family: string) => addPreset(family)}
        />
        <Space direction="vertical" size={6} style={{ width: '100%' }}>
          {vocabulary.kinds.map(({ kind }) => (
            <Card
              key={kind}
              size="small"
              hoverable
              data-testid={`palette-${kind}`}
              onClick={() => addScreen(kind)}
              styles={{ body: { padding: '8px 10px' } }}
            >
              <div style={{ display: 'flex', gap: 8 }}>
                <PlusOutlined style={{ color: 'var(--ol-primary)', marginTop: 3 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center' }}>
                    {kindIcon(kind)} {KIND_COPY[kind]?.label ?? kind}
                  </div>
                  {KIND_COPY[kind] && (
                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                      {KIND_COPY[kind].description}
                    </Text>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </Space>
      </div>

      {/* ---- right: the selected component's properties ---- */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {editingJourney ? (
          <Card size="small" title="The whole journey">
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              <Findings items={[...under(diagnostics, '/journey'), ...under(diagnostics, '/locales')]} base="" />
              <JourneySettings
                value={value}
                vocabulary={vocabulary}
                diagnostics={diagnostics}
                onChange={onChange}
              />
            </Space>
          </Card>
        ) : !current ? (
          <Empty description="Select a screen on the left, or add one from the palette." />
        ) : (
          <Card
            size="small"
            title={
              <Space>
                {kindIcon(current.kind)}
                <span>
                  Screen {selected + 1} ·{' '}
                  {KIND_COPY[current.kind ?? '']?.label ?? current.kind}
                </span>
              </Space>
            }
            extra={
              <Tooltip title="Duplicate this screen (the copy lands right after it)">
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => duplicate(selected)}
                  aria-label={`Duplicate ${current.title ?? 'screen'}`}
                >
                  Duplicate
                </Button>
              </Tooltip>
            }
          >
            <Space direction="vertical" size={14} style={{ width: '100%' }}>
              <Findings items={under(diagnostics, screenPath)} base={screenPath} />
              <div>
                <FieldLabel text="Title" hint="What the applicant sees as this step's heading." />
                <Input
                  value={current.title ?? ''}
                  placeholder="e.g. Upload your photo ID"
                  aria-label="Title"
                  data-testid="screen-title"
                  status={errorAt(diagnostics, `${screenPath}/title`) ? 'error' : undefined}
                  onChange={(e) => {
                    const title = e.target.value;
                    let next = withTitle(current, title);
                    // Seed the id from the title while the id is still auto-ish (draft UX).
                    if (!current.id || current.id === slugify(current.title ?? '')) {
                      const taken = new Set(
                        screens.filter((_, i) => i !== selected).map((s) => s.id ?? ''),
                      );
                      next = { ...next, id: uniqueId(slugify(title), taken) };
                    }
                    replaceScreen(selected, next);
                  }}
                />
                {locales
                  .filter((l) => l !== 'en')
                  .map((locale) => (
                    <div key={locale} style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginTop: 4 }}>
                      <Text type="secondary" className="ol-mono" style={{ fontSize: 11, width: 18 }}>
                        {locale}
                      </Text>
                      <Input
                        aria-label={`Title (${locale})`}
                        dir={locale === 'ar' ? 'rtl' : undefined}
                        value={(current.ui?.text?.title as Record<string, string> | undefined)?.[locale] ?? ''}
                        onChange={(e) =>
                          replaceScreen(selected, withTitleTranslation(current, locale, e.target.value))
                        }
                      />
                    </div>
                  ))}
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ width: 260 }}>
                  <FieldLabel
                    text="Screen id"
                    hint="Stable technical id — referenced by analytics and the case file. Letters, digits, dot, dash, underscore."
                  />
                  <Input
                    className="ol-mono"
                    value={current.id ?? ''}
                    status={errorAt(diagnostics, `${screenPath}/id`) ? 'error' : undefined}
                    onChange={(e) => patchScreen(selected, { id: e.target.value })}
                  />
                </div>
                <div style={{ width: 200 }}>
                  <FieldLabel
                    text="Layout slot"
                    hint="Where the room places this screen: PRIMARY is the main column; SECONDARY and SIDEBAR are supporting positions."
                  />
                  <Select
                    style={{ width: '100%' }}
                    allowClear
                    placeholder="PRIMARY (default)"
                    value={current.layoutSlot}
                    options={vocabulary.layoutSlots.map((s) => ({ value: s, label: s }))}
                    onChange={(layoutSlot) => patchScreen(selected, { layoutSlot })}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <Checkbox
                    data-testid="screen-optional"
                    checked={current.optional === true}
                    onChange={(e) => {
                      const next = { ...current };
                      if (e.target.checked) next.optional = true;
                      else delete next.optional;
                      replaceScreen(selected, next);
                    }}
                  >
                    Optional step
                  </Checkbox>
                </div>
              </div>

              <KindSpecEditor
                screen={current}
                path={screenPath}
                vocabulary={vocabulary}
                locales={locales}
                diagnostics={diagnostics}
                onChange={(updates) => {
                  const next = { ...current, ...updates };
                  if (updates.ui === undefined && 'ui' in updates) delete next.ui;
                  replaceScreen(selected, next);
                }}
              />

              {current.kind === 'FORM' && (
                <FormFieldsEditor
                  fields={current.prompt ?? []}
                  onChange={(prompt) => patchScreen(selected, { prompt })}
                  vocabulary={vocabulary}
                  locales={locales}
                  diagnostics={diagnostics}
                  path={`${screenPath}/prompt`}
                />
              )}

              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: 'words',
                    label: (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Wording on this step
                        {under(diagnostics, `${screenPath}/ui/text`).length > 0 && (
                          <Tag color="warning" style={{ marginInlineStart: 6 }}>
                            {under(diagnostics, `${screenPath}/ui/text`).length}
                          </Tag>
                        )}
                      </Text>
                    ),
                    children: (
                      <Space direction="vertical" size={10} style={{ width: '100%' }} data-testid="step-wording">
                        {(vocabulary.kinds.find((k) => k.kind === current.kind)?.uiSlots ?? [])
                          .filter((slot) => slot !== 'title')
                          .map((slot) => (
                            <LocalizedField
                              key={slot}
                              label={SLOT_COPY[slot]?.label ?? slot}
                              hint={SLOT_COPY[slot]?.hint}
                              value={current.ui?.text?.[slot]}
                              locales={locales}
                              diagnostics={diagnostics}
                              path={`${screenPath}/ui/text/${slot}`}
                              onChange={(text) => replaceScreen(selected, withUiText(current, slot, text))}
                            />
                          ))}
                      </Space>
                    ),
                  },
                ]}
              />

              <Collapse
                ghost
                size="small"
                items={[
                  {
                    key: 'content-keys',
                    label: (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Content keys (advanced — multilingual text from the content ladder)
                      </Text>
                    ),
                    children: (
                      <Space direction="vertical" size={10} style={{ width: '100%' }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Instead of a literal title, a content KEY resolves per language on this
                          hub&apos;s content ladder (author the strings under Admin → content). A
                          key that resolves nowhere fails the publish gate — never a silent blank.
                        </Text>
                        {(['titleKey', 'whyKey', 'helpKey'] as const).map((k) => (
                          <div key={k}>
                            <FieldLabel
                              text={k}
                              hint={
                                k === 'titleKey'
                                  ? 'Replaces the literal title with a resolvable content key.'
                                  : k === 'whyKey'
                                    ? '"Why we ask" copy shown under the screen.'
                                    : 'Help copy for the applicant on this screen.'
                              }
                            />
                            <Input
                              className="ol-mono"
                              value={(current[k] as string) ?? ''}
                              onChange={(e) =>
                                patchScreen(selected, { [k]: e.target.value || undefined })
                              }
                            />
                          </div>
                        ))}
                      </Space>
                    ),
                  },
                ]}
              />
            </Space>
          </Card>
        )}
      </div>
    </div>
  );
}

/**
 * One journey entry: click selects, the HANDLE drags (pointer or keyboard — Space lifts, arrows
 * move, Space drops, Esc cancels; dnd-kit announces each step to screen readers). Duplicate and
 * delete stay one click away.
 */
function SortableScreenCard({
  sortId,
  screen,
  index,
  selected,
  hasError,
  onSelect,
  onDuplicate,
  onDelete,
}: {
  sortId: string;
  screen: ScreenDef;
  index: number;
  selected: boolean;
  hasError: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: sortId });
  const style: CSSProperties = {
    transform: transform
      ? `translate3d(${Math.round(transform.x)}px, ${Math.round(transform.y)}px, 0)`
      : undefined,
    transition: transition ?? undefined,
    opacity: isDragging ? 0.75 : undefined,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };
  const name = screen.title || screen.id || 'screen';
  return (
    <div ref={setNodeRef} style={style}>
      <Card
        size="small"
        onClick={onSelect}
        style={{
          cursor: 'pointer',
          borderColor: selected ? 'var(--ol-primary)' : undefined,
          boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.25)' : undefined,
        }}
        styles={{ body: { padding: '8px 10px' } }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            size="small"
            type="text"
            icon={<HolderOutlined />}
            style={{ cursor: 'grab', touchAction: 'none' }}
            aria-label={`Reorder ${name} — press Space to lift, arrow keys to move, Space to drop`}
            {...attributes}
            {...listeners}
          />
          <span style={{ color: 'var(--ol-text-tertiary)', fontSize: 11, width: 14 }}>
            {index + 1}
          </span>
          {kindIcon(screen.kind)}
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 12.5,
            }}
          >
            {screen.title || screen.titleKey || <Text type="danger">untitled</Text>}
          </span>
          {screen.optional && <Tag style={{ fontSize: 10, marginInlineEnd: 0 }}>optional</Tag>}
          {hasError && (
            <WarningOutlined
              aria-label="The gate refuses something on this screen"
              style={{ color: 'var(--ol-negative, #d4380d)' }}
            />
          )}
          <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
            <Button
              size="small"
              type="text"
              icon={<CopyOutlined />}
              onClick={onDuplicate}
              aria-label={`Duplicate ${name}`}
            />
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={onDelete}
              aria-label={`Delete ${name}`}
            />
          </span>
        </div>
      </Card>
    </div>
  );
}

function FieldLabel({ text, hint }: { text: string; hint: string }) {
  return (
    <div style={{ fontSize: 12, marginBottom: 4, display: 'flex', gap: 6, alignItems: 'center' }}>
      {text}
      <Tooltip title={hint}>
        <Text type="secondary" style={{ fontSize: 11, cursor: 'help' }}>
          ⓘ
        </Text>
      </Tooltip>
    </div>
  );
}

/** Consecutive fields sharing a `section` form one displayed group (order stays author-owned). */
interface FieldRun {
  section: string | null;
  start: number;
  fields: PromptField[];
}

function runsOf(fields: PromptField[]): FieldRun[] {
  const runs: FieldRun[] = [];
  fields.forEach((f, i) => {
    const section = typeof f.section === 'string' && f.section.trim() ? f.section : null;
    const last = runs[runs.length - 1];
    if (last && last.section === section) last.fields.push(f);
    else runs.push({ section, start: i, fields: [f] });
  });
  return runs;
}

/**
 * The FORM component's field list: label-first authoring with legal names seeded automatically,
 * per-field TYPES over the compiler's allowlist (select gets an options editor), and named
 * SECTION groups. Moving a field across a group boundary joins the group it lands in — the
 * FigJam rule: where you drop it is where it belongs.
 */
function FormFieldsEditor({
  fields,
  onChange,
  vocabulary,
  locales,
  diagnostics,
  path,
}: {
  fields: PromptField[];
  onChange: (next: PromptField[]) => void;
  vocabulary: ScreenVocabulary;
  locales: string[];
  diagnostics: ScreenDiagnostic[];
  path: string;
}) {
  const patchField = (index: number, updates: Partial<PromptField>) => {
    const next = fields.slice();
    next[index] = { ...next[index], ...updates };
    onChange(next);
  };
  const move = (index: number, dir: -1 | 1) => {
    const to = index + dir;
    if (to < 0 || to >= fields.length) return;
    const next = fields.slice();
    [next[index], next[to]] = [next[to], next[index]];
    // Join the group you land in: the moved field adopts the section of the field it swapped
    // with, so crossing a boundary is a membership change, never a group-splitting surprise.
    next[to] = { ...next[to], section: next[index].section };
    onChange(next);
  };
  const renameSection = (run: FieldRun, name: string) => {
    const next = fields.slice();
    for (let i = run.start; i < run.start + run.fields.length; i++) {
      next[i] = { ...next[i], section: name || undefined };
    }
    onChange(next);
  };
  const addField = (section: string | null, at: number) => {
    const field: PromptField = { name: '', label: '' };
    if (section) field.section = section;
    onChange([...fields.slice(0, at), field, ...fields.slice(at)]);
  };
  const addSection = () => {
    const taken = new Set(
      fields.map((f) => (typeof f.section === 'string' ? f.section : '')).filter(Boolean),
    );
    let name = 'Section';
    for (let i = 2; taken.has(name); i++) name = `Section ${i}`;
    onChange([...fields, { name: '', label: '', section: name }]);
  };

  const runs = runsOf(fields);

  return (
    <div>
      <FieldLabel
        text="Form fields"
        hint="The questions the applicant answers. The Label is what they read; the Name is the technical key their answer is stored under; the Type is how the room renders the input."
      />
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {runs.map((run) => (
          <div key={`run-${run.start}-${run.section ?? ''}`}>
            {run.section !== null && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  marginBottom: 6,
                  paddingTop: 2,
                }}
              >
                <GroupOutlined style={{ color: 'var(--ol-text-tertiary)' }} />
                <Input
                  size="small"
                  value={run.section}
                  onChange={(e) => renameSection(run, e.target.value)}
                  aria-label="Section name"
                  style={{ width: 240, fontWeight: 600 }}
                />
                <Tooltip title="Remove the section — its fields stay, ungrouped">
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => renameSection(run, '')}
                    aria-label={`Remove section ${run.section}`}
                  />
                </Tooltip>
              </div>
            )}
            <Space
              direction="vertical"
              size={8}
              style={{
                width: '100%',
                paddingInlineStart: run.section !== null ? 20 : 0,
                borderInlineStart:
                  run.section !== null ? '2px solid var(--ol-border)' : undefined,
              }}
            >
              {run.fields.map((f, j) => {
                const i = run.start + j;
                return (
                  <FieldEditorRow
                    key={i}
                    field={f}
                    fieldTypes={vocabulary.fieldTypes}
                    locales={locales}
                    problem={problemOf(diagnostics, `${path}/${i}`)}
                    canUp={i > 0}
                    canDown={i < fields.length - 1}
                    onPatch={(updates) => patchField(i, updates)}
                    onMove={(dir) => move(i, dir)}
                    onDelete={() => onChange(fields.filter((_, k) => k !== i))}
                  />
                );
              })}
              {run.section !== null && (
                <Button
                  size="small"
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => addField(run.section, run.start + run.fields.length)}
                >
                  Add field to {run.section}
                </Button>
              )}
            </Space>
          </div>
        ))}
        <Space>
          <Button size="small" icon={<PlusOutlined />} onClick={() => addField(null, fields.length)}>
            Add field
          </Button>
          <Tooltip title="Group fields under a named heading the applicant sees">
            <Button size="small" icon={<FolderAddOutlined />} onClick={addSection}>
              Add section
            </Button>
          </Tooltip>
        </Space>
      </Space>
    </div>
  );
}

/** The gate's first refusal on this field, in its words. */
function problemOf(diagnostics: ScreenDiagnostic[], path: string): string | null {
  return under(diagnostics, path).find((d) => d.severity === 'ERROR')?.message ?? null;
}

function FieldEditorRow({
  field: f,
  fieldTypes,
  locales,
  problem,
  canUp,
  canDown,
  onPatch,
  onMove,
  onDelete,
}: {
  field: PromptField;
  fieldTypes: string[];
  locales: string[];
  problem: string | null;
  canUp: boolean;
  canDown: boolean;
  onPatch: (updates: Partial<PromptField>) => void;
  onMove: (dir: -1 | 1) => void;
  onDelete: () => void;
}) {
  const type = typeof f.type === 'string' && f.type ? f.type : 'text';
  const isSelect = type === 'select';
  const options = Array.isArray(f.options) ? f.options : [];
  const translations = locales.filter((l) => l !== 'en');
  const setTranslation = (prop: string, locale: string, text: string) => {
    const i18n = { ...(f.i18n ?? {}) };
    const byLocale = { ...(i18n[prop] ?? {}) };
    if (text) byLocale[locale] = text;
    else delete byLocale[locale];
    if (Object.keys(byLocale).length > 0) i18n[prop] = byLocale;
    else delete i18n[prop];
    onPatch({ i18n: Object.keys(i18n).length > 0 ? i18n : undefined });
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <Input
          placeholder="Label the applicant sees, e.g. Annual income"
          value={f.label ?? ''}
          onChange={(e) => {
            const label = e.target.value;
            const updates: Partial<PromptField> = { label };
            if (!f.name || f.name === fieldNameFrom(f.label ?? '')) {
              updates.name = fieldNameFrom(label);
            }
            onPatch(updates);
          }}
          style={{ flex: 1, minWidth: 120 }}
        />
        <Tooltip title={problem ?? 'Technical key for the stored answer'}>
          <Input
            className="ol-mono"
            placeholder="fieldName"
            value={f.name}
            status={problem ? 'error' : undefined}
            prefix={problem ? <WarningOutlined style={{ color: 'var(--ol-danger, #d4380d)' }} /> : null}
            onChange={(e) => onPatch({ name: e.target.value })}
            style={{ width: 170 }}
          />
        </Tooltip>
        <Select
          aria-label="Field type"
          value={type}
          options={fieldTypes.map((value) => ({ value, label: FIELD_TYPE_LABELS[value] ?? value }))}
          style={{ width: 150 }}
          onChange={(next) =>
            onPatch({
              // 'text' is the server default — keep the JSON minimal by omitting it.
              type: next === 'text' ? undefined : next,
              // options belong to select only; the gate warns on strays, so drop them.
              options: next === 'select' ? options : undefined,
            })
          }
        />
        <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={!canUp} onClick={() => onMove(-1)} aria-label={`Move field ${f.label || f.name || ''} up`} />
        <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={!canDown} onClick={() => onMove(1)} aria-label={`Move field ${f.label || f.name || ''} down`} />
        <Button
          size="small"
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={onDelete}
          aria-label={`Delete field ${f.label || f.name || ''}`}
        />
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 4 }}>
        <Checkbox
          checked={f.required === true}
          onChange={(e) => onPatch({ required: e.target.checked ? true : undefined })}
          style={{ fontSize: 12 }}
        >
          <Text type="secondary" style={{ fontSize: 12 }}>
            Required
          </Text>
        </Checkbox>
        {isSelect && (
          <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Select
              mode="tags"
              aria-label="Dropdown options"
              placeholder="Type an option and press Enter"
              value={options}
              suffixIcon={null}
              onChange={(next: string[]) =>
                onPatch({ options: next.map((o) => o.trim()).filter(Boolean) })
              }
              style={{ flex: 1 }}
              tokenSeparators={[',']}
            />
          </div>
        )}
      </div>
      {problem && (
        <div style={{ fontSize: 11.5, marginTop: 2 }} data-testid="field-problem">
          <Text type="danger">
            <WarningOutlined /> {problem}
          </Text>
        </div>
      )}
      <Collapse
        ghost
        size="small"
        items={[
          {
            key: 'more',
            label: (
              <Text type="secondary" style={{ fontSize: 11.5 }}>
                Placeholder, help{translations.length > 0 ? ', translations' : ''}
              </Text>
            ),
            children: (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Input
                  size="small"
                  placeholder="Placeholder inside the field"
                  aria-label="Placeholder"
                  value={f.placeholder ?? ''}
                  onChange={(e) => onPatch({ placeholder: e.target.value || undefined })}
                />
                <Input
                  size="small"
                  placeholder="Help under the field"
                  aria-label="Help text"
                  value={f.helpText ?? ''}
                  onChange={(e) => onPatch({ helpText: e.target.value || undefined })}
                />
                {translations.map((locale) => (
                  <Input
                    key={locale}
                    size="small"
                    addonBefore={locale}
                    dir={locale === 'ar' ? 'rtl' : undefined}
                    placeholder="Label in this language"
                    aria-label={`Label (${locale})`}
                    value={f.i18n?.label?.[locale] ?? ''}
                    onChange={(e) => setTranslation('label', locale, e.target.value)}
                  />
                ))}
              </Space>
            ),
          },
        ]}
      />
    </div>
  );
}
